import { useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GoalDTO, GoalHealth } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, Field, FilterPill, PageHeader, Panel, ProgressBar, ProgressRing, inputClass, toneColor, type Tone } from "@/design/primitives";
import { useAuth } from "@/auth/auth";

const healthTone: Record<GoalHealth, Tone> = { ON_TRACK: "green", AT_RISK: "amber", OFF_TRACK: "red" };

/** Goals & Roadmap (spec §24, /Design): hierarchie, timeline, traceability. */
export function GoalsPage() {
  const navigate = useNavigate();
  // pohled patří do adresy — odkaz na roadmapu jde sdílet
  const search = useSearch({ from: "/shell/goals" }) as { view?: "roadmap" | "trace" };
  const view = search.view ?? "hierarchy";
  const setView = (next: "hierarchy" | "roadmap" | "trace") =>
    void navigate({ to: "/goals", search: next === "hierarchy" ? {} : { view: next } });
  const goals = useQuery({ queryKey: ["goals"], queryFn: api.goals });
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const { session } = useAuth();
  const [creating, setCreating] = useState(false);

  const all = goals.data?.goals ?? [];
  const company = all.filter((g) => g.scope === "COMPANY");
  const projectGoals = all.filter((g) => g.scope === "PROJECT");

  return (
    <div className="af-animate-fade">
      <PageHeader
        title={cs.goals.title}
        kicker="CÍLE"
        lead={cs.goals.lead}
        right={
          <>
            {(
              [
                ["hierarchy", cs.goals.hierarchy],
                ["roadmap", cs.goals.roadmap],
                ["trace", cs.goals.traceability],
              ] as const
            ).map(([key, label]) => (
              <FilterPill
                key={key}
                active={view === key}
                onClick={() => setView(key)}
                testId={`goals-view-${key}`}
              >
                {label}
              </FilterPill>
            ))}
            {session?.user.role === "OWNER" ? (
              <Btn variant="primary" onClick={() => setCreating(true)} data-testid="goals-new">
                {cs.goals.newGoal}
              </Btn>
            ) : null}
          </>
        }
      />

      {creating ? (
        <NewGoalForm
          projects={(projects.data?.projects ?? []).map((p) => ({ id: p.id, name: p.name }))}
          companyGoals={company}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {view === "hierarchy" ? <HierarchyView company={company} projectGoals={projectGoals} /> : null}
      {view === "roadmap" ? <RoadmapView goals={all} /> : null}
      {view === "trace" ? <TraceView /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

function HierarchyView({ company, projectGoals }: { company: GoalDTO[]; projectGoals: GoalDTO[] }) {
  const primary = company[0] ?? null;
  const linked = projectGoals.filter((g) => !primary || g.parentGoalId === primary.id || !g.parentGoalId);
  const initiativeCount = linked.reduce((n, g) => n + g.initiatives.length, 0);
  const taskCount = linked.reduce(
    (n, g) => n + g.initiatives.reduce((m, i) => m + i.taskCount, 0),
    0,
  );

  return (
    <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[360px_1fr]">
      <div className="flex flex-col gap-3.5">
        {primary ? (
          <Panel brackets tone="cyan" className="p-5" data-testid="company-goal">
            <div className="af-label mb-3" style={{ color: "var(--accent)" }}>
              {cs.goals.companyGoal}
              {primary.deadline ? ` // ${new Date(primary.deadline).toLocaleDateString("cs-CZ", { month: "numeric", year: "numeric" })}` : ""}
            </div>
            <div className="af-card-title leading-snug" style={{ color: "var(--text-primary)" }}>
              {primary.title}
            </div>
            <div className="my-4 flex items-center gap-4">
              <ProgressRing value={primary.progress} size={84} tone={healthTone[primary.health]} label={`${primary.progress}%`} />
              <div className="text-[12px] leading-[1.8]" style={{ color: "var(--text-secondary)" }}>
                {primary.metric}{" "}
                <span style={{ color: "var(--text-primary)" }}>
                  {primary.currentValue?.toLocaleString("cs-CZ")} {primary.unit}
                </span>{" "}
                / {primary.targetValue?.toLocaleString("cs-CZ")}
                <br />
                {cs.goals.owner.toLowerCase()} <span style={{ color: "var(--text-primary)" }}>{primary.ownerLabel}</span>
                <br />
                health{" "}
                <span style={{ color: toneColor[healthTone[primary.health]] }}>
                  {cs.goals.health[primary.health]}
                </span>
              </div>
            </div>
            <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {cs.goals.linked(linked.length, initiativeCount, taskCount)}
            </div>
          </Panel>
        ) : (
          <Panel className="p-6">
            <div className="af-label-dim mb-2">{cs.goals.companyGoal}</div>
            <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
              {cs.goals.emptyCompany}
            </div>
          </Panel>
        )}
      </div>

      <div className="flex flex-col gap-3">
        {projectGoals.map((g) => (
          <GoalCard key={g.id} goal={g} />
        ))}
        {projectGoals.length === 0 ? (
          <Panel className="p-6">
            <div className="af-label-dim mb-2">{cs.goals.projectGoals}</div>
            <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
              {cs.goals.emptyProject}
            </div>
          </Panel>
        ) : null}
      </div>
    </div>
  );
}

export function GoalCard({ goal, compact = false }: { goal: GoalDTO; compact?: boolean }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const addInitiative = useMutation({
    mutationFn: () => api.createInitiative(goal.id, { title, note: "" }),
    onSuccess: () => {
      setAdding(false);
      setTitle("");
      void queryClient.invalidateQueries({ queryKey: ["goals"] });
      if (goal.projectId) {
        void queryClient.invalidateQueries({ queryKey: ["section", goal.projectId, "goals"] });
      }
    },
  });
  const tone = healthTone[goal.health];

  return (
    <Panel className="p-4" data-testid="goal-card">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          {!compact ? <span className="af-label-dim">{cs.goals.projectGoal}</span> : null}
          <span className="af-card-title" style={{ color: "var(--text-primary)" }}>
            {goal.title}
          </span>
          {goal.projectSlug ? (
            <Link to="/projects/$slug" params={{ slug: goal.projectSlug }} className="text-[11px]" style={{ color: "var(--accent)" }}>
              {goal.projectName}
            </Link>
          ) : null}
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span style={{ color: toneColor[tone] }}>● {cs.goals.health[goal.health]}</span>
          <span style={{ color: "var(--text-tertiary)" }}>
            {goal.deadline ? new Date(goal.deadline).toLocaleDateString("cs-CZ") : ""}
          </span>
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <ProgressBar value={goal.progress} tone={tone} height={3} className="min-w-[140px] flex-1" />
        <span className="text-[12px]" style={{ color: toneColor[tone] }}>
          {goal.progress} %
        </span>
        {goal.metric ? (
          <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {goal.metric}
            {goal.targetValue ? ` · cíl ${goal.targetValue.toLocaleString("cs-CZ")} ${goal.unit}` : ""}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-1.5">
        {goal.initiatives.map((i) => (
          <div
            key={i.id}
            className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 px-2.5 py-1.5 text-[12px]"
            style={{ borderLeft: "2px solid color-mix(in srgb, var(--accent) 20%, transparent)", background: "var(--surface-inset)" }}
          >
            <span style={{ color: "var(--text-primary)" }}>{i.title}</span>
            <span style={{ color: i.taskCount > 0 && i.doneTaskCount === i.taskCount ? "var(--positive)" : "var(--text-tertiary)" }}>
              {i.taskCount ? `${i.doneTaskCount}/${i.taskCount} tasků` : i.note || "bez tasků"}
            </span>
          </div>
        ))}
        {adding ? (
          <div className="flex items-end gap-2">
            <input
              className={inputClass}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="název iniciativy"
              autoFocus
            />
            <Btn disabled={title.trim().length < 3 || addInitiative.isPending} onClick={() => addInitiative.mutate()}>
              +
            </Btn>
          </div>
        ) : (
          <button
            type="button"
            className="cursor-pointer self-start text-[11px] tracking-[0.1em]"
            style={{ color: "var(--accent)" }}
            onClick={() => setAdding(true)}
          >
            {cs.goals.addInitiative}
          </button>
        )}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function RoadmapView({ goals }: { goals: GoalDTO[] }) {
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const accessible = projects.data?.projects ?? [];
  // milestones všech přístupných projektů
  const milestoneQueries = useQuery({
    queryKey: ["roadmap-milestones", accessible.map((p) => p.id).join(",")],
    enabled: accessible.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        accessible.map(async (p) => ({
          project: p,
          milestones: (await api.milestones(p.id)).milestones,
        })),
      );
      return results.flatMap((r) =>
        r.milestones.map((m) => ({ ...m, projectName: r.project.name, projectSlug: r.project.slug })),
      );
    },
  });

  const rows = useMemo(() => {
    const items: {
      key: string;
      name: string;
      note: string;
      noteTone: Tone;
      deadline: Date | null;
      atRisk: boolean;
      progress: number;
    }[] = [];
    for (const m of milestoneQueries.data ?? []) {
      if (m.status === "DONE") continue;
      items.push({
        key: `m-${m.id}`,
        name: `${m.projectName} · ${m.title}`,
        note: m.atRisk ? "deadline risk" : `${m.progress} % hotovo`,
        noteTone: m.atRisk ? "amber" : "dim",
        deadline: m.deadline ? new Date(m.deadline) : null,
        atRisk: m.atRisk,
        progress: m.progress,
      });
    }
    for (const g of goals) {
      if (!g.deadline) continue;
      items.push({
        key: `g-${g.id}`,
        name: g.projectName ? `${g.projectName} · ${g.title}` : g.title,
        note: `goal · ${g.progress} %`,
        noteTone: healthTone[g.health],
        deadline: new Date(g.deadline),
        atRisk: g.health !== "ON_TRACK",
        progress: g.progress,
      });
    }
    return items.sort((a, b) => (a.deadline?.getTime() ?? 0) - (b.deadline?.getTime() ?? 0));
  }, [milestoneQueries.data, goals]);

  // časová osa: dnes − 7 dní → +90 dní
  const start = Date.now() - 7 * 86_400_000;
  const span = 97 * 86_400_000;
  const months = useMemo(() => {
    const list: { label: string; left: number }[] = [];
    const d = new Date(start);
    d.setDate(1);
    for (let i = 0; i < 4; i++) {
      const left = ((d.getTime() - start) / span) * 100;
      if (left >= 0 && left < 100) {
        list.push({ label: d.toLocaleDateString("cs-CZ", { month: "long" }).toUpperCase(), left });
      }
      d.setMonth(d.getMonth() + 1);
    }
    return list;
  }, [start, span]);
  const todayLeft = ((Date.now() - start) / span) * 100;

  const atRiskRow = rows.find((r) => r.atRisk);

  return (
    <Panel className="p-5" data-testid="roadmap">
      {/* časová osa je široká — pod md se scrolluje horizontálně */}
      <div className="overflow-x-auto">
      <div className="min-w-[640px]">
      <div className="relative mb-2 ml-[206px] h-4">
        {months.map((m) => (
          <span
            key={m.label}
            className="absolute text-[11px] tracking-[0.2em]"
            style={{ left: `${m.left}%`, color: "var(--text-tertiary)" }}
          >
            {m.label}
          </span>
        ))}
      </div>
      <div className="relative">
        <div
          className="absolute bottom-0 top-0 w-px"
          style={{ left: `calc(206px + (100% - 206px) * ${todayLeft / 100})`, background: "var(--positive)", opacity: 0.6, zIndex: 2 }}
        />
        <span
          className="absolute text-[11px] tracking-[0.1em]"
          style={{ left: `calc(206px + (100% - 206px) * ${todayLeft / 100})`, top: -14, color: "var(--positive)", transform: "translateX(-50%)" }}
        >
          {cs.goals.today}
        </span>
        {rows.map((r) => {
          const deadlineLeft = r.deadline ? Math.min(97, Math.max(2, ((r.deadline.getTime() - start) / span) * 100)) : null;
          const barWidth = deadlineLeft != null ? Math.max(4, (deadlineLeft * r.progress) / 100) : 0;
          return (
            <div key={r.key} className="grid grid-cols-[190px_1fr] items-center gap-x-4 py-2.5" style={{ borderTop: "1px solid color-mix(in srgb, var(--accent) 5%, transparent)" }}>
              <div>
                <div className="text-[12px]" style={{ color: "var(--text-primary)" }}>
                  {r.name}
                </div>
                <div className="text-[11px]" style={{ color: toneColor[r.noteTone] }}>
                  {r.note}
                </div>
              </div>
              <div className="relative h-4">
                <div
                  className="absolute top-[5px] h-1.5"
                  style={{ borderRadius: "var(--radius-box)",
                    left: 0,
                    width: `${barWidth}%`,
                    background: r.atRisk ? "color-mix(in srgb, var(--warning) 50%, transparent)" : "color-mix(in srgb, var(--accent) 35%, transparent)",
                    border: `1px solid ${r.atRisk ? "color-mix(in srgb, var(--warning) 60%, transparent)" : "color-mix(in srgb, var(--accent) 40%, transparent)"}`,
                  }}
                />
                {deadlineLeft != null ? (
                  <>
                    <div
                      className="absolute top-[1px] h-2 w-2"
                      style={{
                        left: `${deadlineLeft}%`,
                        transform: "rotate(45deg) translateX(-4px)",
                        background: r.atRisk ? "var(--warning)" : "var(--accent)",
                        boxShadow: `0 0 8px ${r.atRisk ? "color-mix(in srgb, var(--warning) 60%, transparent)" : "color-mix(in srgb, var(--accent) 50%, transparent)"}`,
                      }}
                    />
                    <span
                      className="absolute whitespace-nowrap text-[11px]"
                      style={{ left: `${deadlineLeft}%`, top: -1, marginLeft: 10, color: r.atRisk ? "var(--warning)" : "var(--accent)" }}
                    >
                      {r.deadline!.toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric" })}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
          );
        })}
        {rows.length === 0 ? (
          <div className="py-6 text-center text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            Žádné milestones ani goals s deadlinem.
          </div>
        ) : null}
      </div>
      </div>
      </div>
      {atRiskRow ? (
        <div className="mt-3.5 px-3.5 py-2.5 text-[11px] leading-relaxed" style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--warning) 25%, transparent)", background: "color-mix(in srgb, var(--warning) 3%, transparent)", color: "var(--text-secondary)" }}>
          ⚠ <span style={{ color: "var(--warning)" }}>{cs.goals.riskNote}</span> {atRiskRow.name} — současné tempo nestačí.{" "}
          <Link to="/decisions" style={{ color: "var(--accent)" }}>
            Decision Inbox →
          </Link>
        </div>
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function TraceView() {
  const goals = useQuery({ queryKey: ["goals"], queryFn: api.goals });
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });

  // ukázková cesta: první project goal s iniciativou, která má tasky
  const chain = useMemo(() => {
    const gs = goals.data?.goals ?? [];
    const projectGoal = gs.find((g) => g.scope === "PROJECT" && g.initiatives.some((i) => i.taskCount > 0)) ?? gs.find((g) => g.scope === "PROJECT");
    const companyGoal = gs.find((g) => g.id === projectGoal?.parentGoalId) ?? gs.find((g) => g.scope === "COMPANY");
    const initiative = projectGoal?.initiatives.find((i) => i.taskCount > 0) ?? projectGoal?.initiatives[0];
    return { companyGoal, projectGoal, initiative };
  }, [goals.data]);

  const projectName = chain.projectGoal?.projectName ?? "";

  const steps = [
    chain.companyGoal
      ? { tag: "COMPANY GOAL", name: chain.companyGoal.title, sub: `${chain.companyGoal.progress} % · ${chain.companyGoal.metric}`, tone: "cyan" as Tone, link: "vede k" }
      : null,
    chain.projectGoal
      ? { tag: "PROJECT GOAL", name: chain.projectGoal.title, sub: `${projectName} · ${chain.projectGoal.progress} %`, tone: "cyan" as Tone, link: "naplňuje" }
      : null,
    chain.initiative
      ? { tag: "INICIATIVA", name: chain.initiative.title, sub: `${chain.initiative.doneTaskCount}/${chain.initiative.taskCount} tasků`, tone: "violet" as Tone, link: "realizují" }
      : null,
    chain.initiative && chain.initiative.taskCount > 0
      ? { tag: "TASKY", name: `${chain.initiative.taskCount} tasků v kanbanu`, sub: `${projectName} · per codebase subtasky`, tone: "green" as Tone, link: null }
      : null,
  ].filter((x): x is NonNullable<typeof x> => x !== null);

  void projects;

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_340px]">
      <Panel className="p-6" data-testid="trace-chain">
        <div className="af-label-dim mb-4">{cs.goals.traceNote}</div>
        <div className="flex max-w-[640px] flex-col">
          {steps.map((s, i) => (
            <div key={s.tag}>
              <div className="px-4 py-3" style={{ borderRadius: "var(--radius-box)", border: `1px solid ${toneColor[s.tone]}40`, background: `${i === 0 ? "color-mix(in srgb, var(--accent) 3%, transparent)" : "transparent"}` }}>
                <div className="mb-1 flex justify-between">
                  <span className="text-[11px] tracking-[0.18em]" style={{ color: toneColor[s.tone] }}>
                    {s.tag}
                  </span>
                  <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                    {s.sub}
                  </span>
                </div>
                <div className="af-display text-[13px] font-semibold" style={{ color: "var(--text-primary)" }}>
                  {s.name}
                </div>
              </div>
              {s.link ? (
                <div className="flex items-center gap-2.5 py-0.5 pl-6">
                  <div className="h-5 w-px" style={{ background: "color-mix(in srgb, var(--accent) 30%, transparent)" }} />
                  <span className="text-[11px] tracking-[0.1em]" style={{ color: "var(--accent)" }}>
                    {s.link.toUpperCase()}
                  </span>
                </div>
              ) : null}
            </div>
          ))}
          {steps.length === 0 ? (
            <div className="py-6 text-center text-[12px]" style={{ color: "var(--text-tertiary)" }}>
              {cs.goals.empty}
            </div>
          ) : null}
        </div>
      </Panel>
      <div className="flex flex-col gap-3.5 self-start">
        <Panel className="p-4">
          <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
            {cs.goals.traceBidirectional}
          </div>
          <p className="text-[12px] leading-[1.75]" style={{ color: "var(--text-secondary)" }}>
            {cs.goals.traceBidirectionalNote}
          </p>
        </Panel>
        <Panel tone="amber" className="p-4">
          <div className="af-label mb-2" style={{ color: "var(--warning)" }}>
            {cs.goals.noGoalTasks}
          </div>
          <p className="text-[12px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Tasky bez vazby na goal Factory označí v panelu Traceability, ale neblokuje je
            (klientské zakázky goal strukturu mít nemusí).
          </p>
        </Panel>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function NewGoalForm({
  projects,
  companyGoals,
  onClose,
}: {
  projects: { id: string; name: string }[];
  companyGoals: GoalDTO[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<"COMPANY" | "PROJECT">("PROJECT");
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");
  const [parentGoalId, setParentGoalId] = useState(companyGoals[0]?.id ?? "");
  const [title, setTitle] = useState("");
  const [metric, setMetric] = useState("");
  const [target, setTarget] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.createGoal({
        scope,
        projectId: scope === "PROJECT" ? projectId : null,
        parentGoalId: scope === "PROJECT" && parentGoalId ? parentGoalId : null,
        title,
        metric,
        targetValue: target ? Number(target) : null,
        currentValue: target ? 0 : null,
        unit: "",
        description: "",
        health: "ON_TRACK",
        ownerLabel: "",
        status: "ACTIVE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["goals"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <Panel brackets className="mb-4 max-w-3xl p-4" data-testid="goal-new-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[120px_1fr_180px_150px]">
        <Field label={cs.fields.scope}>
          <select className={inputClass} style={{ background: "var(--surface-inset)" }} value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
            <option value="PROJECT">PROJECT</option>
            <option value="COMPANY">COMPANY</option>
          </select>
        </Field>
        <Field label={cs.fields.name}>
          <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus data-testid="goal-new-title" />
        </Field>
        <Field label={cs.fields.metric}>
          <input className={inputClass} value={metric} onChange={(e) => setMetric(e.target.value)} placeholder="MRR / konverze…" />
        </Field>
        <Field label={cs.fields.targetNumber}>
          <input className={inputClass} value={target} onChange={(e) => setTarget(e.target.value)} type="number" />
        </Field>
      </div>
      {scope === "PROJECT" ? (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={cs.fields.project}>
            <select className={inputClass} style={{ background: "var(--surface-inset)" }} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={cs.fields.linkToCompanyGoal}>
            <select className={inputClass} style={{ background: "var(--surface-inset)" }} value={parentGoalId} onChange={(e) => setParentGoalId(e.target.value)}>
              <option value="">—</option>
              {companyGoals.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
          </Field>
        </div>
      ) : null}
      {error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <Btn onClick={onClose}>{cs.common.cancel}</Btn>
        <Btn variant="primary" disabled={title.trim().length < 3 || mutation.isPending} onClick={() => mutation.mutate()} data-testid="goal-new-save">
          {cs.common.create}
        </Btn>
      </div>
    </Panel>
  );
}

export { healthTone };
